import React, { memo, useCallback, useMemo } from 'react';
import type { ContentBlock, FileInfo } from '../../types/contentBlock';
import { extractPreviewSource } from '../../utils/codePreviewExtractor';
import './PreviewButton.css';

/** 可预览的 MIME 类型集合 */
const PREVIEWABLE_MIME_TYPES: ReadonlySet<string> = new Set([
  'text/html',
  'text/css',
  'text/javascript',
  'application/javascript',
  'application/x-javascript',
]);

/**
 * 检测 files 列表中是否包含可预览的文件（HTML/CSS/JS）
 */
const hasPreviewableFile = (files?: readonly FileInfo[]): boolean => {
  if (!files || files.length === 0) return false;
  return files.some(f => PREVIEWABLE_MIME_TYPES.has(f.mime_type));
};

export interface PreviewButtonProps {
  /** 消息文本内容 */
  readonly content: string;
  /** 结构化内容块 */
  readonly contentBlocks?: readonly ContentBlock[];
  /** 文件列表 */
  readonly files?: readonly FileInfo[];
  /** 点击预览回调 */
  readonly onClickPreview: () => void;
}

/**
 * 预览按钮（放在时间行）
 * 内部自动判断消息是否包含可预览的 HTML 内容，无则不渲染
 *
 * 判断来源（任一满足即显示）：
 * 1. content 中有 HTML/CSS/JS 代码块
 * 2. contentBlocks 中有可预览文件
 * 3. files 中有 mime_type 为 text/html 等的文件
 */
export const PreviewButton: React.FC<PreviewButtonProps> = memo(({
  content,
  contentBlocks,
  files,
  onClickPreview,
}) => {
  const hasPreview = useMemo(() => {
    // 来源 1 + 2：从 content 和 contentBlocks 中检测
    const source = extractPreviewSource(content, contentBlocks);
    if (source.hasPreview) return true;

    // 来源 3：从 files 字段中检测
    return hasPreviewableFile(files);
  }, [content, contentBlocks, files]);

  const handleClick = useCallback((): void => {
    onClickPreview();
  }, [onClickPreview]);

  if (!hasPreview) return null;

  return (
    <button
      type="button"
      className="msg-preview-btn"
      onClick={handleClick}
      title="预览网页效果"
    >
      <svg className="msg-preview-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
      <span>预览</span>
    </button>
  );
});

PreviewButton.displayName = 'PreviewButton';